import { PortalContainerContext } from "@istari-frontend/shared";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { ArrowDownLeft, ArrowUpRight, KeyRound, PanelLeft, Save, Sparkles, XIcon } from "lucide-react";
import { useId, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";

import { openLlmKeyPrompt, useLlmApiKey } from "~/ai-chat/data-source/llm-api-key-store";
import { AiChatComposer } from "~/ai-chat/feature/ai-chat-composer";
import { AiChatHistorySidebar } from "~/ai-chat/feature/ai-chat-history-sidebar";
import { AiChatTranscript } from "~/ai-chat/feature/ai-chat-transcript";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useAiChatWindowSize } from "~/ai-chat/feature/use-ai-chat-window-size";
import { useChatPopout } from "~/ai-chat/feature/use-chat-popout";
import { useChatPopoutMode } from "~/ai-chat/feature/use-chat-popout-mode";
import { useChatSend } from "~/ai-chat/feature/use-chat-send";
import { useSaveChatResource } from "~/ai-chat/feature/use-save-chat-resource";
import type { ChatMessage, ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { LlmApiKeyDialog } from "~/ai-chat/ui/llm-api-key-dialog";
import { routes } from "~/routes";
import { cn } from "~/utils/cn";

export function AiChatFloatingWidget() {
  const { isOpen, isAvailable, open, close } = useAiChat();
  const location = useLocation();
  const isSystemDashboardRoute =
    location.pathname === routes.SystemsDashboard || location.pathname.startsWith(`${routes.SystemsDashboard}/`);

  // Once a chat call returns 404 ("AI not configured on this server"), the
  // launcher hides for the rest of the session. If the user already had the
  // window open when that happened, keep it open so they can see the inline
  // error in the transcript.
  if (!isAvailable && !isOpen) return null;

  if (isSystemDashboardRoute && !isOpen) return null;

  return isOpen ? <AiChatWindow onClose={close} /> : <AiChatLauncher onOpen={open} />;
}

function AiChatLauncher({ onOpen }: { onOpen: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          onClick={onOpen}
          aria-label="Open Istari AI"
          className={cn(
            "fixed right-4 bottom-4 z-50 size-12 rounded-full p-0 shadow-lg sm:right-6 sm:bottom-6",
            "transition-shadow duration-150 hover:shadow-xl",
          )}
        >
          <Sparkles className="size-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">Chat with Istari</TooltipContent>
    </Tooltip>
  );
}

function AiChatWindow({ onClose }: { onClose: () => void }) {
  const { size, isResizing, resizeHandleProps } = useAiChatWindowSize();
  const { popoutMode } = useChatPopoutMode();
  const { popoutWindow, isPoppedOut, toggle } = useChatPopout(size, popoutMode);

  const portalContainer = isPoppedOut && popoutWindow ? popoutWindow.document.body : null;
  const panel = (
    <PortalContainerContext.Provider value={portalContainer}>
      <ChatPanel isPoppedOut={isPoppedOut} onTogglePopout={toggle} onClose={onClose} />
    </PortalContainerContext.Provider>
  );

  // Popped out: render the chat into the floating window (React portal keeps it in the same
  // component tree, so chat state/context is shared). The in-app corner widget yields to it.
  if (isPoppedOut && popoutWindow) {
    return createPortal(
      <div className="bg-background text-foreground flex h-full flex-col">{panel}</div>,
      popoutWindow.document.body,
    );
  }

  return (
    <aside
      aria-label="Istari Chat"
      style={{ width: size.width, height: size.height }}
      className={cn(
        "border-border bg-background fixed z-50 flex flex-col rounded-xl border shadow-2xl",
        "right-4 bottom-4 sm:right-6 sm:bottom-6",
        "max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)]",
        "sm:max-h-[calc(100vh-3rem)] sm:max-w-[calc(100vw-3rem)]",
        "animate-in fade-in slide-in-from-bottom-2 zoom-in-95 duration-150",
        isResizing && "select-none",
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            {...resizeHandleProps}
            role="separator"
            aria-label="Resize chat window"
            tabIndex={0}
            className={cn(
              "border-muted-foreground/70 hover:border-muted-foreground absolute -top-1 -left-1 z-10",
              "size-5 cursor-nwse-resize touch-none rounded-tl-lg border-t-4 border-l-4 transition-colors",
              "focus-visible:ring-ring outline-none focus-visible:ring-2",
            )}
          />
        </TooltipTrigger>
        <TooltipContent side="left">Drag to resize, double-click to reset</TooltipContent>
      </Tooltip>
      {panel}
    </aside>
  );
}

function ChatPanel({
  isPoppedOut,
  onTogglePopout,
  onClose,
}: {
  isPoppedOut: boolean;
  onTogglePopout: () => void;
  onClose: () => void;
}) {
  const { messages, chatTitle } = useAiChat();
  const { send, isPending, activity } = useChatSend();
  const { hasKey } = useLlmApiKey();
  const { save, isSaving } = useSaveChatResource();
  const [historyOpen, setHistoryOpen] = useState(false);
  const hasMessages = messages.some((item) => item.kind === "message");

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-border flex shrink-0 items-center justify-between gap-2 border-b-[0.5px] px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className={cn("px-1.5", historyOpen && "text-primary")}
                onClick={() => setHistoryOpen((prev) => !prev)}
                aria-label={historyOpen ? "Hide chat history" : "Show chat history"}
                aria-pressed={historyOpen}
              >
                <PanelLeft className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{historyOpen ? "Hide history" : "Show history"}</TooltipContent>
          </Tooltip>
          <Sparkles className="text-muted-foreground size-4 shrink-0" />
          <ChatTitle />
        </div>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="px-1.5"
                onClick={() => save(chatTitle ?? chatWindowTitle(messages), messages)}
                disabled={!hasMessages || isSaving}
                aria-label="Save chat to your workspace"
              >
                <Save className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Save chat to your workspace</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className={cn("px-1.5", hasKey && "text-primary")}
                onClick={openLlmKeyPrompt}
                aria-label={hasKey ? "Update your LLM key" : "Set your LLM key"}
              >
                <KeyRound className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{hasKey ? "Update your LLM key" : "Set your LLM key"}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="px-1.5"
                onClick={onTogglePopout}
                aria-label={isPoppedOut ? "Dock chat back into the app" : "Open chat in a separate window"}
              >
                {isPoppedOut ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isPoppedOut ? "Dock back into the app" : "Open in a separate window"}
            </TooltipContent>
          </Tooltip>
          {/* Hidden when popped out: the detached window's own close (and the dock button) both return
              the chat to the corner, so a "close the whole chat" control here — right next to the OS
              close button — is confusing and destroys the conversation. Dock first, then close. */}
          {!isPoppedOut && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="xs" className="px-1.5" onClick={onClose} aria-label="Close Istari AI">
                  <XIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close</TooltipContent>
            </Tooltip>
          )}
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        {historyOpen && (
          <div className="border-border w-56 shrink-0 overflow-y-auto border-r-[0.5px]">
            <AiChatHistorySidebar />
          </div>
        )}
        <div className="flex min-h-0 flex-1 flex-col">
          <AiChatTranscript
            isPending={isPending}
            activity={activity}
            onSuggestionSelect={(suggestion) => void send(suggestion)}
          />
          <AiChatComposer onSend={(content) => void send(content)} isPending={isPending} />
        </div>
      </div>
      <LlmApiKeyDialog />
    </div>
  );
}

// Editable before the conversation is created (the value is used as the create title); read-only
// once persisted, since there is no rename endpoint yet.
function ChatTitle() {
  const { activeConversationId, chatTitle, setChatTitle, messages } = useAiChat();
  const titleId = useId();

  if (activeConversationId === null) {
    return (
      <input
        id={titleId}
        aria-label="Chat title"
        value={chatTitle ?? ""}
        onChange={(event) => setChatTitle(event.target.value.length > 0 ? event.target.value : null)}
        placeholder="Name this chat…"
        className={cn(
          "text-foreground placeholder:text-muted-foreground min-w-0 flex-1 truncate bg-transparent",
          "text-sm font-medium outline-none",
        )}
      />
    );
  }

  return <span className="truncate text-sm font-medium">{chatTitle ?? chatWindowTitle(messages)}</span>;
}

// Title the window after the first thing the user asked (falling back to "New chat"), so an open
// conversation is recognizable at a glance instead of staying "New chat" forever.
export function chatWindowTitle(items: ChatTranscriptItem[]): string {
  const firstUserMessage = items.find((item): item is ChatMessage => item.kind === "message" && item.role === "user");
  const words = firstUserMessage?.content.trim().split(/\s+/).slice(0, 6).join(" ") ?? "";
  if (!words) return "New chat";
  return words.length > 40 ? `${words.slice(0, 40).trimEnd()}…` : words;
}
